import {Controller} from "@hotwired/stimulus";
import {useClickOutside} from "stimulus-use";
import debounce from "lodash/debounce";

/**
 * Responsive menu controller
 * - allows you to dynamically show/hide the menu display/hide button based
 *   on the menu width and viewport width
 * - allows to open and close a drop down menu on a button click.
 * Drop down menu can be closed on clicking outside as well.
 *
 *  %nav.invisible{data: { controller: 'responsive-menu',
 *                         responsive_menu_expanded_class: 'expanded',
 *                         responsive_menu_collapsed_class: 'collapsed',
 *                         responsive_menu_hidden_class: 'hidden!',
 *                         responsive_menu_invisible_class: 'invisible',
 *                         responsive_menu_invisible_class: 'invisible',
 *                         turbo_prefetch: 'false', }}
 *
 *    %ul{data: { responsive_menu_target: 'content' }}
 *      %li.main-menu-item Menu item 1
 *      %li.main-menu-item Menu item 2
 *      %li.main-menu-item Menu item 3
 *
 *    %button.block.ml-auto.mr-0{aria: { expanded: 'false', label: 'Menu' },
 *                               data: { action: 'click->responsive-menu#toggle', responsive_menu_target: 'toggler' }
 *
 * If the menu layout contains a logo and a dropdown menu icon, you need to mark
 * them as targets for this controller:
 *   responsive_menu_target: 'dropdown'
 *   responsive_menu_target: 'logo'
 *
 * We need their dimensions to calculate the width of the responsive menu itself.
 */

export default class extends Controller {
  static targets = [
    'content',
    'navigation',
    'toggler',
    'logo',
    'dropdown',
    'menuLogoContainer',
  ];
  static classes = [
    'expanded',
    'collapsed',
    'hidden',
    'invisible'
  ];

  togglerHeight = 0;

  connect() {
    useClickOutside(this);

    this.clickOutsideListener = this._clickOutsideListener.bind(this);

    this.resizeListener = this._resizeListener.bind(this);
    window.addEventListener('resize', debounce(this.resizeListener, 100));

    this._loadListener();
  }

  disconnect() {
    super.disconnect();
    window.removeEventListener('resize', this.resizeListener);
  }

  toggle(event) {
    if (event.currentTarget.ariaExpanded === 'true') {
      this._removeClickOutsideListener();
      this._hideMenu();
    } else {
      this._addClickOutsideListener();
      this._showMenu();
    }
  }

  _resizeListener() {
    this._setMenuState();
  }

  _loadListener() {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => {
        this._setMenuState();
      });
    } else {
      document.removeEventListener("DOMContentLoaded", () => {
        this._setMenuState();
      });
      this._setMenuState();
    }
  }

  _clickOutsideListener(event) {
    if (!event.target.closest('[data-responsive-menu-target="toggler"]')) {
      this._hideMenu();
    }
  }

  _setMenuState() {
    /* Make nav and ul invisible to avoid flickering */
    this.element.classList.add(this.invisibleClass);
    this.contentTarget.classList.add(this.invisibleClass);

    /* Remove the hidden class to show the menu */
    this.contentTarget.classList.remove(this.hiddenClass);

    /* Hide toggler to calculate the menu width */
    this._hideToggler();

    /* Expand menu to take measurements */
    this._expandMenu();

    /* Wait for the next frame to ensure all styles are applied */
    setTimeout(() => {
      /* Calculate the width of the menu and container */
      const menuWidth = this._calculateMenuWidth();
      const logoWidth = this._calculateLogoWidth();
      const dropdownWidth = this._calculateDropdownWidth();
      const contentWidth = this._calculateContainerWidth() - logoWidth - dropdownWidth;

      let state = 'expanded';

      /* Toggler height = menu height */
      this.togglerHeight = this._calculateTogglerHeight();

      /* If menu doesn't fit, collapse it */
      if (contentWidth - menuWidth < 0) {
        this._collapseMenu();
        this._showToggler();
        this._hideMenu();
        state = 'collapsed';
      }

      /* Adjust css classes based on the state */
      this._setMenuLogoContainerClass(state);

      /* Reveal menu */
      this.element.classList.remove(this.invisibleClass);
      this.contentTarget.classList.remove(this.invisibleClass);
    }, 1)
  }

  /* When the hamburger menu is displayed,
     move it to the end of the element. */
  _setMenuLogoContainerClass(state) {
    if (this.hasMenuLogoContainerTarget) {
      if (state === 'expanded') {
        this.menuLogoContainerTarget.classList.remove('justify-between');
        this.menuLogoContainerTarget.classList.add('justify-start');
      } else {
        this.menuLogoContainerTarget.classList.remove('justify-start');
        this.menuLogoContainerTarget.classList.add('justify-between');
      }
    }
  }

  _calculateContainerWidth() {
    return this.element.offsetWidth;
  }

  _calculateMenuWidth() {
    return this.contentTarget.offsetWidth;
  }

  _calculateLogoWidth() {
    return this.hasLogoTarget ? this.logoTarget.offsetWidth : 0;
  }

  _calculateDropdownWidth() {
    return this.hasDropdownTarget ? this.dropdownTarget.offsetWidth : 0;
  }

  _calculateTogglerHeight() {
    return this.contentTarget.offsetHeight;
  }

  _expandMenu() {
    this.navigationTarget.classList.remove(this.collapsedClass);
    this.navigationTarget.classList.add(this.expandedClass);
  }

  _collapseMenu() {
    this.navigationTarget.classList.remove(this.expandedClass);
    this.navigationTarget.classList.add(this.collapsedClass);
  }

  _showMenu() {
    this.togglerTarget.ariaExpanded = true;
    this.contentTarget.classList.remove(this.hiddenClass);
  }

  _hideMenu() {
    this.togglerTarget.ariaExpanded = false;
    this.contentTarget.classList.add(this.hiddenClass);
  }

  _showToggler() {
    this.togglerTarget.classList.remove(this.hiddenClass);
    this.togglerTarget.style.height = this.togglerHeight + 'px';
    this._calculateMenuPosition(false);
  }

  _hideToggler() {
    this.togglerTarget.classList.add(this.hiddenClass);
    this._calculateMenuPosition();
  }

  _calculateMenuPosition(expanded = true) {
    if (expanded) {
      /* Remove menu position */
      this.contentTarget.style.right = 0;
      this.contentTarget.style.top = 0;
    } else {
      setTimeout(() => {
        /* Calculate menu position for collapsed state */
        let top = this.togglerTarget.offsetTop + this.togglerTarget.offsetHeight;
        const right = window.innerWidth
          - this.togglerTarget.getBoundingClientRect().right
          + parseFloat(window.getComputedStyle(this.togglerTarget).paddingRight);
        if (this.hasDropdownTarget) {
          top -= this.dropdownTarget.offsetHeight / 2;
        }

        this.contentTarget.style.right = `${right}px`;
        this.contentTarget.style.top = `${top}px`;
        this.contentTarget.classList.add(this.hiddenClass);
      })
    }
  }

  _addClickOutsideListener() {
    document.addEventListener('click', this.clickOutsideListener);
  }

  _removeClickOutsideListener() {
    document.removeEventListener('click', this.clickOutsideListener);
  }
}
